import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Gavel, Loader2, ScrollText, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");
const words = (s) => String(s || "").replace(/_/g, " ");

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

const TONE = {
  lodged: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  panel_appointed: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  decided: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  withdrawn: "border-border bg-muted text-muted-foreground",
};

export const Challenges = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/elections/${electionRef}/challenges`);
    setData(res.data);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setDialog(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return (
    <Panel title="Challenges and appeals" testId="panel-challenges-loading">
      <p className="text-[12.5px] text-muted-foreground">Opening the challenge file…</p>
    </Panel>
  );

  const picked = dialog?.picked || [];

  return (
    <div className="space-y-4">
      <Panel title="Challenges and appeals" testId="panel-challenges"
             kicker={data.window?.closes_at
               ? `${data.items.length} on file · the ${data.window.days}-day window ${data.window.open ? "closes" : "closed"} ${pretty(data.window.closes_at)}`
               : `${data.items.length} on file · nothing can be challenged until the result is declared`}
             actions={data.may_lodge && (
               <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                       data-testid="lodge-challenge"
                       onClick={() => setDialog({ kind: "lodge", ground: "counting",
                         statement: "", remedy_sought: "", position_ref: "" })}>
                 <Gavel className="mr-1.5 h-3.5 w-3.5" /> Lodge a challenge
               </Button>
             )}>
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          A recount only re-counts the same papers. A challenge objects to the way the election
          itself was run. The Commission cannot judge its own election, so a panel of{" "}
          {data.panel_size} with no part in it hears the objection and the majority decides.
        </p>
        {!data.may_lodge && data.why_not && (
          <p className="mt-2 text-[12px] text-muted-foreground" data-testid="challenge-why-not">
            {data.why_not}
          </p>
        )}
        {data.items.length === 0 ? (
          <p className="mt-3 text-[12.5px] text-muted-foreground" data-testid="challenges-empty">
            No challenge has been lodged against this election.
          </p>
        ) : (
          <div className="mt-3 space-y-3" data-testid="challenge-list">
            {data.items.map((c) => (
              <div key={c.challenge_ref} data-testid={`challenge-${c.challenge_ref}`}
                   className="rounded-xl border border-border px-3.5 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-[12.5px] font-medium text-foreground">
                      {c.ground_label}
                      {c.position_title ? ` — ${c.position_title}` : ""}
                    </p>
                    <p className="text-[10.5px] text-muted-foreground">
                      {c.challenge_ref} · lodged by {c.lodged_by_name} as a {c.standing} on{" "}
                      {pretty(c.created_at)}
                    </p>
                  </div>
                  <Chip className={TONE[c.status] || TONE.withdrawn}
                        data-testid={`challenge-status-${c.challenge_ref}`}>
                    {words(c.status)}
                  </Chip>
                </div>

                <p className="mt-2 text-[12px] leading-relaxed text-foreground"
                   data-testid={`challenge-statement-${c.challenge_ref}`}>{c.statement}</p>
                {c.remedy_sought && (
                  <p className="mt-1 text-[11.5px] text-muted-foreground">
                    Remedy sought: {c.remedy_sought}
                  </p>
                )}

                {c.answer?.text && (
                  <div className="mt-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2"
                       data-testid={`challenge-answer-${c.challenge_ref}`}>
                    <p className="kicker">The Commission's answer</p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      {c.answer.text} — {c.answer.by}, {pretty(c.answer.at)}
                    </p>
                  </div>
                )}

                {(c.panel || []).length > 0 && (
                  <div className="mt-2.5">
                    <p className="kicker">The panel</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {c.panel.map((m) => (
                        <Chip key={m.email} className="border-border bg-muted text-muted-foreground"
                              data-testid={`panel-member-${m.email}`}>{m.name}</Chip>
                      ))}
                    </div>
                  </div>
                )}

                {(c.findings || []).length > 0 && (
                  <div className="mt-2.5 space-y-1" data-testid={`challenge-findings-${c.challenge_ref}`}>
                    <p className="kicker">Findings</p>
                    {c.findings.map((f) => (
                      <p key={f.email} className="text-[11.5px] text-muted-foreground">
                        <span className="font-medium text-foreground">{f.name}</span> —{" "}
                        {f.outcome_label}: {f.finding}
                      </p>
                    ))}
                  </div>
                )}

                {c.decision?.outcome && (
                  <div className="mt-2.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
                       data-testid={`challenge-decision-${c.challenge_ref}`}>
                    <p className="text-[12px] font-medium text-foreground">
                      {c.decision.label} · decided by {c.decision.majority} on{" "}
                      {pretty(c.decision.decided_at)}
                    </p>
                    {c.decision.recount_request_ref && (
                      <p className="text-[11.5px] text-muted-foreground">
                        A recount is authorised as {c.decision.recount_request_ref}.
                      </p>
                    )}
                    {c.decision.rerun_election_ref && (
                      <p className="text-[11.5px] text-muted-foreground">
                        The re-run is opened as {c.decision.rerun_election_ref} and needs a fresh
                        Commission.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {data.may_appoint_panel && c.status === "lodged" && (
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11.5px]"
                            data-testid={`appoint-panel-${c.challenge_ref}`}
                            onClick={() => setDialog({ kind: "panel", ref: c.challenge_ref,
                              pool: c.panel_pool || [], picked: [], minute: "" })}>
                      <Users className="mr-1.5 h-3.5 w-3.5" /> Appoint the panel
                    </Button>
                  )}
                  {c.you_may_answer && (
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11.5px]"
                            data-testid={`answer-challenge-${c.challenge_ref}`}
                            onClick={() => setDialog({ kind: "answer", ref: c.challenge_ref,
                              answer: "" })}>
                      <ScrollText className="mr-1.5 h-3.5 w-3.5" /> Answer it
                    </Button>
                  )}
                  {c.you_may_find && (
                    <Button size="sm" className="h-7 rounded-full text-[11.5px]"
                            data-testid={`record-finding-${c.challenge_ref}`}
                            onClick={() => setDialog({ kind: "finding", ref: c.challenge_ref,
                              outcome: "dismissed", finding: "" })}>
                      <ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Record your finding
                    </Button>
                  )}
                  {c.you_may_withdraw && (
                    <Button size="sm" variant="ghost" className="h-7 rounded-full text-[11.5px]"
                            data-testid={`withdraw-challenge-${c.challenge_ref}`}
                            onClick={() => setDialog({ kind: "withdraw", ref: c.challenge_ref,
                              reason: "" })}>
                      Withdraw it
                    </Button>
                  )}
                  {c.status === "decided" && (
                    <a className="inline-flex h-7 items-center rounded-full border border-border px-3 text-[11.5px] text-foreground hover:border-primary/40"
                       href={`${API_BASE}/elections/challenges/${c.challenge_ref}/notice.pdf`}
                       target="_blank" rel="noreferrer"
                       data-testid={`challenge-notice-${c.challenge_ref}`}>
                      Decision notice (PDF)
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Dialog open={dialog?.kind === "lodge"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="challenge-dialog">
          <DialogHeader>
            <DialogTitle>Lodge a challenge</DialogTitle>
            <DialogDescription>
              Say plainly what went wrong and what you want done about it. A panel of{" "}
              {data.panel_size} with no part in this election will hear it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="On what ground">
              <Select value={dialog?.ground || ""}
                      onValueChange={(v) => setDialog((d) => ({ ...d, ground: v }))}>
                <SelectTrigger data-testid="challenge-ground"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(data.grounds).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Which post, if it is about one"
               hint="Leave it blank if the objection is about the whole election.">
              <Select value={dialog?.position_ref || "none"}
                      onValueChange={(v) => setDialog((d) => ({ ...d,
                        position_ref: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="challenge-post"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">The whole election</SelectItem>
                  {data.positions.map((p) => (
                    <SelectItem key={p.position_ref} value={p.position_ref}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="What happened" hint="At least 40 characters, in your own words.">
              <Textarea rows={5} data-testid="challenge-statement"
                        value={dialog?.statement || ""}
                        onChange={(e) => setDialog((d) => ({ ...d, statement: e.target.value }))} />
            </F>
            <F label="What you are asking for">
              <Input data-testid="challenge-remedy" value={dialog?.remedy_sought || ""}
                     placeholder="A recount, a re-run of one post, or the election set aside"
                     onChange={(e) => setDialog((d) => ({ ...d,
                       remedy_sought: e.target.value }))} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="challenge-save" disabled={busy === "lodge"}
                    onClick={() => run("lodge",
                      () => api.post(`/elections/${electionRef}/challenges`, {
                        ground: dialog.ground, statement: dialog.statement,
                        remedy_sought: dialog.remedy_sought,
                        position_ref: dialog.position_ref }),
                      "Your challenge is lodged")}>
              {busy === "lodge" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Lodge it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "panel"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="panel-dialog">
          <DialogHeader>
            <DialogTitle>Appoint the panel</DialogTitle>
            <DialogDescription>
              Exactly {data.panel_size} people. The Commission, every candidate and whoever lodged
              the challenge are all barred.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {(dialog?.pool || []).map((p) => (
              <label key={p.email}
                     className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-[12px]">
                <input type="checkbox" data-testid={`panel-pick-${p.email}`}
                       checked={picked.includes(p.email)}
                       onChange={(e) => setDialog((d) => ({ ...d,
                         picked: e.target.checked ? [...d.picked, p.email]
                           : d.picked.filter((x) => x !== p.email) }))} />
                <span className="text-foreground">{p.name}</span>
                <span className="text-muted-foreground">{p.email}</span>
              </label>
            ))}
          </div>
          <p className="text-[11.5px] text-muted-foreground" data-testid="panel-count">
            {picked.length} of {data.panel_size} picked
          </p>
          <Textarea rows={2} data-testid="panel-minute" placeholder="Anything for the record"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog((d) => ({ ...d, minute: e.target.value }))} />
          <DialogFooter>
            <Button data-testid="panel-save"
                    disabled={busy === "panel" || picked.length !== data.panel_size}
                    onClick={() => run("panel",
                      () => api.post(`/elections/challenges/${dialog.ref}/panel`,
                        { emails: picked, minute: dialog.minute }),
                      "The panel is appointed")}>
              {busy === "panel" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Appoint them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "answer"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="answer-dialog">
          <DialogHeader>
            <DialogTitle>The Commission's answer</DialogTitle>
            <DialogDescription>
              Your account of what happened, put to the panel and to whoever objected.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={5} data-testid="answer-text" value={dialog?.answer || ""}
                    onChange={(e) => setDialog((d) => ({ ...d, answer: e.target.value }))} />
          <DialogFooter>
            <Button data-testid="answer-save" disabled={busy === "answer"}
                    onClick={() => run("answer",
                      () => api.post(`/elections/challenges/${dialog.ref}/answer`,
                        { answer: dialog.answer }), "Your answer is on the file")}>
              {busy === "answer" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              File it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "finding"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="finding-dialog">
          <DialogHeader>
            <DialogTitle>Your finding</DialogTitle>
            <DialogDescription>
              Yours alone. The majority of the panel settles the challenge.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="What you find">
              <Select value={dialog?.outcome || ""}
                      onValueChange={(v) => setDialog((d) => ({ ...d, outcome: v }))}>
                <SelectTrigger data-testid="finding-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(data.outcomes).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Why" hint="At least 20 characters. It is printed on the decision notice.">
              <Textarea rows={4} data-testid="finding-text" value={dialog?.finding || ""}
                        onChange={(e) => setDialog((d) => ({ ...d, finding: e.target.value }))} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="finding-save" disabled={busy === "finding"}
                    onClick={() => run("finding",
                      () => api.post(`/elections/challenges/${dialog.ref}/finding`,
                        { outcome: dialog.outcome, finding: dialog.finding }),
                      "Your finding is recorded")}>
              {busy === "finding" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "withdraw"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw your challenge</DialogTitle>
            <DialogDescription>Say why, for the record.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} data-testid="withdraw-reason" value={dialog?.reason || ""}
                    onChange={(e) => setDialog((d) => ({ ...d, reason: e.target.value }))} />
          <DialogFooter>
            <Button data-testid="withdraw-save" disabled={busy === "withdraw"}
                    onClick={() => run("withdraw",
                      () => api.post(`/elections/challenges/${dialog.ref}/withdraw`,
                        { reason: dialog.reason }), "Your challenge is withdrawn")}>
              {busy === "withdraw" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Withdraw it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
